import React, { useState, useEffect, useMemo, useRef } from 'react'
import ReactDOM from 'react-dom'
import Lottie from 'react-lottie'
import { useWebsocket } from '~components/WebsocketEvents'
import { useThemeUpdate } from 'shared-ui/assets/styles/PortalThemeProvider'
import IntlUtil from 'shared-ui/utils/IntlUtil'
import { getPointsLabel } from 'shared-ui/utils/points'
import { useMember } from 'shared-ui/components/pages/PageWrapper'

const earnedSound = '/sounds/earned.mp3'
const burnedSound = '/sounds/burned.mp3'
const DEBOUNCE_DELAY = 500 // Wait 0.5 second after last change before showing animation

const PointsAnimation = ({ enableSound }) => {
  const { jpointsChanges, setJpointsChanges } = useWebsocket()
  const [currentAnimation, setCurrentAnimation] = useState(null)
  const [isWaitingForChanges, setIsWaitingForChanges] = useState(false)
  const [stableJpointsChanges, setStableJpointsChanges] = useState([])
  const { colorScheme } = useThemeUpdate()
  const [lottieJson, setLottieJson] = useState(null)
  const [lastColorScheme, setLastColorScheme] = useState(colorScheme)
  const member = useMember()
  const audioRef = useRef(null)
  const soundTimeoutRef = useRef(null)
  const debounceTimerRef = useRef(null)
  const previousLengthRef = useRef(0)

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }

    if (jpointsChanges.length > 0 && jpointsChanges.length !== previousLengthRef.current) {
      previousLengthRef.current = jpointsChanges.length
      setIsWaitingForChanges(true)

      debounceTimerRef.current = setTimeout(() => {
        setStableJpointsChanges([...jpointsChanges])
        setIsWaitingForChanges(false)
      }, DEBOUNCE_DELAY)
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [jpointsChanges])

  useEffect(() => {
    if (stableJpointsChanges.length > 0 && !currentAnimation && !isWaitingForChanges) {
      // Combine all the points into a single animation object
      const combinedAnimation = {
        ...stableJpointsChanges[0], // Base properties from first item
        allChanges: stableJpointsChanges, // Store all changes for reference
      }

      setCurrentAnimation(combinedAnimation)
      setStableJpointsChanges([])
      setJpointsChanges([])

      previousLengthRef.current = 0
    }
  }, [stableJpointsChanges, currentAnimation, isWaitingForChanges, setJpointsChanges])

  // Clear animation after it completes
  useEffect(() => {
    if (currentAnimation) {
      const timer = setTimeout(() => {
        setCurrentAnimation(null)
      }, 2000)

      return () => clearTimeout(timer)
    }
  }, [currentAnimation])

  // Reset everything on account change
  useEffect(() => {
    setCurrentAnimation(null)
    setLottieJson(null)
    setIsWaitingForChanges(false)
    setStableJpointsChanges([])
    previousLengthRef.current = 0

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }

    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      audioRef.current = null
    }

    if (soundTimeoutRef.current) {
      clearTimeout(soundTimeoutRef.current)
      soundTimeoutRef.current = null
    }
  }, [member?.selectedAccountId])

  // Dynamically load the appropriate Lottie JSON.
  useEffect(() => {
    if (!currentAnimation && colorScheme !== lastColorScheme) return

    const loadAnimation = async () => {
      const isNegative = currentAnimation?.difference < 0
      const animationFile = isNegative
        ? colorScheme === 'dark'
          ? import('./lottie/animation-burned-points-dark.json')
          : import('./lottie/animation-burned-points-light.json')
        : colorScheme === 'dark'
          ? import('./lottie/animation-earned-points-dark.json')
          : import('./lottie/animation-earned-points-light.json')

      const module = await animationFile
      setLottieJson(module.default)
    }

    loadAnimation()
  }, [currentAnimation])

  useEffect(() => {
    setLastColorScheme(colorScheme)
  }, [colorScheme])

  const updatedLottieJson = useMemo(() => {
    if (!lottieJson || !currentAnimation) return null

    const updatedJson = JSON.parse(JSON.stringify(lottieJson))

    const changes = currentAnimation.allChanges || [currentAnimation]
    const isNegative = currentAnimation.difference < 0

    if (updatedJson.assets) {
      updatedJson.assets.forEach(asset => {
        if (asset.layers && asset.layers.length > 0) {
          const textLayers = asset.layers.filter(layer => layer.ty === 5)

          if (textLayers.length > 0 && changes.length > 0) {
            const totalTextLayers = textLayers.length
            const totalChanges = Math.min(changes.length, totalTextLayers)

            let startIndex = Math.floor((totalTextLayers - totalChanges) / 2)
            if (startIndex < 0) startIndex = 0

            textLayers.forEach((layer, index) => {
              const textKeyframe = layer.t?.d?.k?.[0]
              if (textKeyframe?.s) {
                const changeIndex = index - startIndex

                if (changeIndex >= 0 && changeIndex < totalChanges) {
                  const point = changes[changeIndex]

                  textKeyframe.s.t = `${point.difference} ${point.type}`

                  if (point.color) {
                    const [r, g, b] = point.color
                      .substring(1)
                      .match(/.{2}/g)
                      .map(hex => parseInt(hex, 16) / 255)
                    textKeyframe.s.fc = [r, g, b]
                  }
                } else {
                  textKeyframe.s.t = ''
                }
              }
            })
          }
        }
      })
    }

    return updatedJson
  }, [currentAnimation, lottieJson])

  useEffect(() => {
    if (!currentAnimation || !lottieJson || !enableSound) return

    const isNegative = currentAnimation.difference < 0
    const soundFile = isNegative ? burnedSound : earnedSound
    const audio = new Audio(soundFile)
    audioRef.current = audio

    if (isNegative) {
      audio.play().catch(err => console.warn('Audio autoplay blocked:', err))
    } else {
      soundTimeoutRef.current = setTimeout(() => {
        audio.play().catch(err => console.warn('Audio autoplay blocked:', err))
      }, 1000)
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.currentTime = 0
        audioRef.current = null
      }

      if (soundTimeoutRef.current) {
        clearTimeout(soundTimeoutRef.current)
        soundTimeoutRef.current = null
      }
    }
  }, [currentAnimation, lottieJson, enableSound, member?.selectedAccountId])

  if (!currentAnimation || !lottieJson) return null

  return ReactDOM.createPortal(
    <div className="pointer-events-none fixed inset-0 z-[2147483647] flex h-screen w-full items-center justify-center">
      <Lottie
        options={{ loop: false, autoplay: true, animationData: updatedLottieJson }}
        width="100%"
        height="100%"
      />
    </div>,
    document.body,
  )
}

export default PointsAnimation
